import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  UtilitiesNotificationResourceTargetType,
  UtilitiesNotificationUserResponse,
  UtilitiesPaginationInfo
} from '@libs/generated-types/utilities';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { UsersUserResponse as User } from 'libs/shared/generated/src/lib/types/users';
import { MenuItem } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { Popover, PopoverModule } from 'primeng/popover';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { StyleClassModule } from 'primeng/styleclass';
import { SvgComponent } from '../../@core/components/svg/svg.component';
import { GlobalSearchService } from '../../services/global-search.service';
import { LayoutService } from '../../services/layout.service';
import { NotificationsService } from '../../services/notifications.service';
import { UsersService } from '../../services/users.service';
import { WebcontentService } from '../../services/webcontent.service';
import { AppSearch } from '../app.search';
@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    FormsModule,
    StyleClassModule,
    PopoverModule,
    TranslateModule,
    BreadcrumbModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    ButtonModule,
    MenuModule,
    AvatarModule,
    SvgComponent,
    ProgressSpinnerModule,
    OverlayBadgeModule,
    BadgeModule,
    AppSearch,
  ],
  template: ` <div class="layout-topbar">
    <div></div>
    <div style="width: 30%;">
      <app-search></app-search>
    </div>
    <div class="layout-topbar-actions">
      <div class="topbar-action-container">
        <p-popover
          #notificationsPopover
          [appendTo]="notificationsAppendTo"
          (onHide)="closeNotifications()"
          styleClass="notifications-popover"
        >
          <ng-template #content>
            <div
              *ngIf="loadingNotifications"
              class="fixed inset-0 bg-black bg-opacity-25 z-40 flex items-center justify-center"
            >
              <div class="bg-white p-6 rounded-lg shadow-lg flex items-center gap-3">
                <p-progressSpinner styleClass="w-8 h-8" strokeWidth="4" fill="transparent" animationDuration=".5s">
                </p-progressSpinner>
              </div>
            </div>
            <div
              class="flex flex-col gap-4 max-h-[80vh] overflow-y-auto"
              *ngIf="notifications && notifications.length > 0"
            >
              <div>
                <ul class="list-none p-0 m-0 flex flex-col">
                  <ng-container *ngFor="let notification of notifications">
                    <li
                      [ngClass]="notification.hasBeenReadByUser ? 'notification-read' : 'notification-not-read'"
                      class="flex items-center flex-col gap-2 px-4 py-3 hover:bg-emphasis cursor-pointer border border-solid border-gray-100"
                    >
                      <div class="flex items-center">
                        <img [src]="notification?.payload?.media?.url" style="width: 80px; margin-right: 20px" />
                        <div style="max-width: 300px;">
                          <span class="font-medium">{{ notification.payload.text }}</span>
                        </div>
                      </div>
                      <br />
                      <div class="flex justify-between w-full">
                        <span>
                          {{ notification.scheduledAt | date: 'dd/MM/YYYY, HH:mm' }}
                        </span>
                        <p-button (click)="clickNotification(notification)" *ngIf="notification.relatedResource">
                        {{'show.global.label' | translate}}</p-button>
                      </div>
                    </li>
                  </ng-container>
                </ul>
                <div class="text-center my-3">
                  <a
                    class="underline cursor-pointer"
                    *ngIf="notificationPagination.hasNext"
                    (click)="getUserNotifications()"
                    >{{ 'topbar.notifications.show-more' | translate }}</a
                  >
                </div>
              </div>
            </div>
            <div *ngIf="!notifications || notifications.length === 0" class="p-4">
              <span>{{ 'topbar.notifications.empty' | translate }}</span>
            </div>
          </ng-template>
        </p-popover>
        <div>
          <p-button
            #notificationsAppendTo
            [rounded]="true"
            [text]="true"
            (onClick)="openNotifications($event)"
            [badge]="notReadNotificationsCount"
            badgeSeverity="danger"
          >
            <app-svg styleClass="h-[20px] w-[20px]" href="/assets/svg/menu/notification.svg"></app-svg>
          </p-button>
        </div>
        <!-- TODO: add badge with unread chat counter -->
        <p-button [rounded]="true" [text]="true" (onClick)="navigateToChat()">
          <i class="pi pi-comment topbar-action-icon"></i>
        </p-button>
        <p-button [rounded]="true" [text]="true" (onClick)="logout()">
          <i class="pi pi-sign-out topbar-action-icon"></i>
        </p-button>
      </div>
      <div class="layout-profile-button" (click)="navigateToProfile()">
        <p-avatar [label]="getUserInitials()" styleClass="mr-2" shape="circle"></p-avatar>
        <div class="layout-profile-text">
          <span class="font-bold">
            {{ getTruncatedName(user?.firstName) }} {{ getTruncatedName(user?.lastName) }}
          </span>
          <!-- TODO: use generated enum -->
          <span>{{ user?.company }}</span>
        </div>
      </div>
    </div>
  </div>`,
  styles: [
    `
      .notification-not-read {
        background-color: #e2e8f0;
      }

      ::ng-deep .p-popover-content {
        padding: 0 !important;
      }
      ::ng-deep .p-popover.p-component.notifications-popover {
        margin-right: 2rem !important;
      }
      ::ng-deep .p-popover.p-component.search-popover {
        min-width: 700px !important;
      }
    `,
  ],
})
export class AppTopbar implements OnInit {
  user: User | null = null;

  loadingNotifications: boolean = false;
  notifications: UtilitiesNotificationUserResponse[] = [];
  notReadNotificationsCount = '0';
  notificationPagination: Partial<UtilitiesPaginationInfo> = {
    page: 0,
    size: 5,
    hasNext: true,
  };

  breadcrumbItems: MenuItem[] = [];
  home: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  profileMenuItems: MenuItem[] = [];

  @ViewChild('notificationsPopover') notificationsPopover!: Popover;
  @ViewChild('searchPopover') searchPopover!: Popover;

  constructor(
    public layoutService: LayoutService,
    private userService: UsersService,
    private readonly contentService: WebcontentService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private translate: TranslateService,
    private oidcSecurityService: OidcSecurityService,
    private notificationService: NotificationsService,
    private searchService: GlobalSearchService,
  ) {
    this.profileMenuItems = [
      { label: translate.instant('topbar.profile.label'), icon: 'pi pi-user', routerLink: '/about-me' },
      { label: translate.instant('topbar.logout.label'), icon: 'pi pi-sign-out', command: () => this.logout() },
    ];
  }

  ngOnInit(): void {
    this.setLoggedUser();
    this.route.url.subscribe(() => {
      this.setBreadcrumb();
    });
    this.setBreadcrumb();

    this.notificationService.getUserNotifications({ size: 1, unread: true, getTotalCount: true }).subscribe({
      next: (data) => {
        this.notReadNotificationsCount = data.totalCount?.toString() || '0';
      },
      error: (error) => {
        console.error('Error fetching notifications:', error);
      },
    });
  }

  setLoggedUser() {
    try {
      this.userService.getUsersMe().subscribe({
        next: (user) => {
          this.user = user;
        },
        error: (error) => {
          console.error('Error fetching current user:', error);
          this.user = null;
        },
      });
    } catch (error) {
      console.error('Error fetching current user:', error);
      this.user = null;
    }
  }

  getUserInitials(): string {
    if (!this.user) return '';
    const firstNameInitial = this.user.firstName ? this.user.firstName.charAt(0).toUpperCase() : '';
    const lastNameInitial = this.user.lastName ? this.user.lastName.charAt(0).toUpperCase() : '';
    return `${firstNameInitial}${lastNameInitial}`;
  }

  setBreadcrumb() {
    let route = this.route.root;
    const items: MenuItem[] = [];
    let url = '';
    while (route.firstChild) {
      route = route.firstChild;
      if (route.snapshot.url.length) {
        url += `/${route.snapshot.url.map((segment) => segment.path).join('/')}`;
        const label = this.translate.instant(
          route.snapshot.data['breadcrumb'] || route.snapshot.url.map((segment) => segment.path).join('/'),
        );
        items.push({ label, routerLink: url });
      }
    }
    this.breadcrumbItems = items;
  }

  toggleDarkMode() {
    this.layoutService.layoutConfig.update((state) => ({ ...state, darkTheme: !state.darkTheme }));
  }

  logout() {
    const loginOidc = localStorage.getItem('oidc-login') ?? undefined;
    this.oidcSecurityService.logoff(loginOidc).subscribe(() => {
      console.log('Logoff completed');
      localStorage.removeItem('oidc-login');
    });
  }

  navigateToProfile() {
    this.router.navigate(['/me']);
  }

  getTruncatedName(name?: string, maxLength: number = 12): string {
    if (!name) return '';
    return name.length > maxLength ? name.slice(0, maxLength) + '…' : name;
  }

  openNotifications(event: any) {
    this.loadingNotifications = true;
    this.notificationsPopover.toggle(event);
    this.getUserNotifications();
  }

  getUserNotifications() {
    this.notificationService
      .getUserNotifications({
        page: this.notificationPagination.page ? this.notificationPagination.page + 1 : 1,
        size: this.notificationPagination.size ?? 5,
        getTotalCount: true,
      })
      .subscribe({
        next: (data) => {
          const items: UtilitiesNotificationUserResponse[] = data.items || [];
          this.notifications = [...this.notifications, ...items];
          this.loadingNotifications = false;
          this.notificationPagination = data.paginationInfo;
        },
        error: (error) => {
          console.error('Error fetching notifications:', error);
          this.loadingNotifications = false;
        },
      });
  }

  clickNotification(notification: UtilitiesNotificationUserResponse) {
    this.notificationsPopover?.hide();

    if (!notification.hasBeenReadByUser) {
      this.notificationService.markAsRead(notification.id).subscribe({
        next: () => {
          notification.hasBeenReadByUser = true;
          const notReadCount = parseInt(this.notReadNotificationsCount) - 1;
          this.notReadNotificationsCount = notReadCount > 0 ? notReadCount.toString() : '0';
        },
        error: (error: any) => {
          console.error('Error marking notification as read:', error);
        },
      });
    }
    const relatedResource = notification.relatedResource;
    if (relatedResource) {
      if ('feedId' in relatedResource) {
        this.router.navigate(['/board', relatedResource.feedId]);
        return;
      }

      switch (relatedResource.type) {
        case UtilitiesNotificationResourceTargetType.BOARD_FEED:
          this.router.navigate(['/board']);
          break;
        case UtilitiesNotificationResourceTargetType.GLOBAL_FEED:
          this.router.navigate(['/board', relatedResource.id]);
          break;
        case UtilitiesNotificationResourceTargetType.CONTEST:
          this.router.navigate(['/contests', relatedResource.id]);
          break;
        case UtilitiesNotificationResourceTargetType.GROUP:
          this.router.navigate(['/community', relatedResource.id]);
          break;
        case UtilitiesNotificationResourceTargetType.CONTENT:
          this.contentService.getById(relatedResource.id).subscribe({
            next: (data) => {
              if (data.slug) {
                this.router.navigate(['/contents', data.slug]);
              } else {
                this.router.navigate(['/contents', relatedResource.id]);
              }
            },
          });
          break;
        case UtilitiesNotificationResourceTargetType.CHAT:
          this.router.navigate(['/chats']);
          break;
      }
    }
  }

  closeNotifications() {
    const notReadNotifications = this.notifications.filter((n) => !n.hasBeenReadByUser);
    if (notReadNotifications.length > 0) {
      this.notificationService.bulkMarkAsRead(notReadNotifications.map((n) => n.id)).subscribe({
        next: () => {
          const newNotReadCount = parseInt(this.notReadNotificationsCount) - notReadNotifications.length || 0;
          this.notReadNotificationsCount = newNotReadCount < 0 ? '0' : newNotReadCount.toString();
        },
        error: (error: any) => {
          console.error('Error marking notifications as read:', error);
        },
      });
    }
    this.notifications = [];
    this.notificationPagination = {
      page: 0,
      size: 5,
      hasNext: true,
    };
  }
  navigateToChat() {
    this.router.navigate(['/chats']);
  }
}
